import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Typography,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Paper,
  ThemeProvider,
  createTheme,
  Tooltip,
  Fade
} from '@mui/material';
import {
  Link as LinkIcon,
  ContentCopy as ContentCopyIcon,
  VpnKey as VpnKeyIcon,
  Public as PublicIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { encrypt } from '../Componentes/FunctionLogin';
import swal from 'sweetalert';
import logopng from '../recursos/logocompleto.png';

// Theme - consistent with Login.js and Contrasena.js
const theme = createTheme({
  palette: {
    primary: {
      main: '#BD1F34', // Talsa Red
      dark: '#8a1525',
      light: '#e04f5f',
    },
    text: {
      primary: '#2D3436',
      secondary: '#636E72',
    },
    background: {
      default: '#F7F9FC',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Outfit", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: {
      fontWeight: 700,
      letterSpacing: '-0.5px'
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '50px',
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: '0 4px 14px 0 rgba(189, 31, 52, 0.39)',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          backgroundColor: '#F7F9FC',
          '& fieldset': { borderColor: 'transparent' },
          '&:hover fieldset': { borderColor: '#e0e0e0' },
          '&.Mui-focused fieldset': { borderColor: '#BD1F34' },
        },
      },
    },
  },
});

export default function Encrypt() {
  const [t] = useTranslation("global");
  const [nit, setNit] = useState('');
  const [nitEncriptado, setNitEncriptado] = useState(''); // Stores the full link
  const [providerType, setProviderType] = useState('nacional');
  const [loading, setLoading] = useState(false);

  const handleGenerateLink = async () => {
    if (!nit) {
      swal(t("link_generator.swal_atencion"), t("link_generator.swal_ingrese_nit"), "warning");
      return;
    }

    setLoading(true);
    try {
      // Determine base URL dynamically
      const origin = window.location.origin; // e.g., https://proveedores.citalsa.com or localhost
      const path = providerType === 'nacional'
        ? '/proveedores/registro'
        : '/proveedores/registrointernacional';

      // Encrypt parameters for 'uniqueness' or security if needed
      // The original logic used `encrypt(nit)`, let's keep it to generate a unique token
      const encryptedToken = await encrypt(nit);

      // Construct Link
      // Note: Adjust query param as needed. Using 'id' as per previous logic, or unique reference.
      const fullLink = `${origin}${path}?id=${encodeURIComponent(encryptedToken)}`;

      setNitEncriptado(fullLink);
      swal(t("link_generator.swal_generado"), t("link_generator.swal_link_generado"), "success");

    } catch (error) {
      console.error("Error generating link:", error);
      swal(t("link_generator.swal_error"), t("link_generator.swal_error_generar"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!nitEncriptado) return;

    if (navigator.clipboard && window.isSecureContext) {
      // Secure context - use modern API
      navigator.clipboard.writeText(nitEncriptado)
        .then(() => {
          swal(t("link_generator.swal_copiado_titulo"), t("link_generator.swal_copiado_desc"), "success");
        })
        .catch((err) => {
          console.error('Async: Could not copy text: ', err);
          fallbackCopyTextToClipboard(nitEncriptado);
        });
    } else {
      // Non-secure or older browser - use fallback
      fallbackCopyTextToClipboard(nitEncriptado);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Ensure it's not visible but part of DOM
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "0";
    document.body.appendChild(textArea);

    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        swal(t("link_generator.swal_copiado_titulo"), t("link_generator.swal_copiado_desc"), "success");
      } else {
        swal("Error", "No se pudo copiar automáticamente. Por favor seleccione y copie manualmente.", "error");
      }
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
      swal("Error", "No se pudo copiar automáticamente.", "error");
    }

    document.body.removeChild(textArea);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        minHeight: '80vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: '#F0F2F5',
        py: 4
      }}>
        <Container maxWidth="sm">
          <Card sx={{
            borderRadius: 4,
            boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
            overflow: 'visible'
          }}>
            {/* Header Icon */}
            <Box sx={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              mt: -4
            }}>
              <Paper
                elevation={4}
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: 'linear-gradient(135deg, #BD1F34 0%, #8a1525 100%)',
                  color: 'white'
                }}
              >
                <LinkIcon sx={{ fontSize: 40 }} />
              </Paper>
            </Box>

            <CardContent sx={{ px: { xs: 3, sm: 5 }, pb: 5, textAlign: 'center' }}>
              <Box sx={{ mb: 3 }}>
                <img src={logopng} alt="Talsa" style={{ height: 35, marginBottom: 16, opacity: 0.8 }} />
                <Typography variant="h5" color="text.primary" gutterBottom>
                  {t("link_generator.titulo")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("link_generator.subtitulo")}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'left' }}>

                <FormControl fullWidth variant="outlined">
                  <InputLabel>{t("link_generator.tipo_proveedor")}</InputLabel>
                  <Select
                    value={providerType}
                    onChange={(e) => setProviderType(e.target.value)}
                    label={t("link_generator.tipo_proveedor")}
                    startAdornment={
                      <InputAdornment position="start">
                        {providerType === 'nacional' ? <BusinessIcon color="action" /> : <PublicIcon color="action" />}
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="nacional">{t("link_generator.nacional")}</MenuItem>
                    <MenuItem value="internacional">{t("link_generator.internacional")}</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label={t("link_generator.referencia_nit")}
                  variant="outlined"
                  fullWidth
                  value={nit}
                  onChange={(e) => setNit(e.target.value)}
                  placeholder={t("link_generator.placeholder_nit")}
                  helperText={t("link_generator.helper_text")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKeyIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleGenerateLink}
                  disabled={loading}
                >
                  {loading ? t("link_generator.btn_generando") : t("link_generator.btn_generar")}
                </Button>

                {nitEncriptado && (
                  <Fade in={true}>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" sx={{ ml: 1, fontWeight: 600, color: 'text.secondary' }}>
                        {t("link_generator.link_generado")}
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={nitEncriptado}
                        multiline
                        maxRows={3}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Copiar Link">
                                <IconButton onClick={handleCopy} edge="end" color="primary">
                                  <ContentCopyIcon />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                          sx: {
                            bgcolor: '#FFF',
                            border: '1px dashed #BD1F34',
                            '& fieldset': { border: 'none' }
                          }
                        }}
                      />
                    </Box>
                  </Fade>
                )}

              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
